import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Injector, ViewChild } from '@angular/core';
import { BaseAdminPanelComponent } from "../admin-panel/base-admin-panel/base-admin-panel.component";
import { DiagnosticsService, ObjectDiagnosticsModel } from "../../services/diagnostics.service";
import { EditObjectsComponent } from "../admin-panel/Objects/edit-objects.component";
import { ObjectsService } from "../../services/objects.service";
import { FilterData } from "../shared/components/objects-filter/objects-filter.component"
import { ObjectFilterType } from '../shared/pipes/FilterObjects.pipe';
import { ReverseGeocoderService } from '../mobile-system/services/reverse-geocoder.service';
import { ObjectGroupViewModel } from '../admin-panel/ObjectGroupViewModel';
import { IManagableObject } from '../admin-panel/IManagableObject';
import {GroupFilterComponent} from "../shared/components/group-filter/group-filter.component";
import {ObjectGroupAccessComponent} from "../shared/components/object-group-access/object-group-access.component";
import {AccessRightsService, GroupAclMaps} from "../../services/access-rights.service";
import {CompanyModel} from "../../services/companies.service";
import {ICoordinatesViewModel} from "../mobile-models/profile-models";
import { PageInfo } from '../shared/components/pagination/pagination.component';

@Component({
  selector: 'app-diagnostics',
  templateUrl: './diagnostics.component.html',
  styleUrls: ['./diagnostics.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DiagnosticsComponent extends BaseAdminPanelComponent implements AfterViewInit {

  private diagnosticsService: DiagnosticsService;

  public override pageInfoFilter = Object.assign(new PageInfo(), { itemsPerPage: 50 });

  loadedObjects: ObjectDiagnosticsModel[] = [];
  ShowStatus = false;

  public filterType: ObjectFilterType = ObjectFilterType.Name;

  private objectsService: ObjectsService;
  private geocodeApi = inject(ReverseGeocoderService);

  oldTimestampCount = 0;
  blockedCount = 0;
  now = (new Date()).getTime();
  day = 3600000 * 24;

  private _cd = inject(ChangeDetectorRef);
  private readonly accessRights = inject(AccessRightsService);

  public companies: CompanyModel[] = [];
  public selectedCompanyIds: number[] = [];

  public groups: ObjectGroupViewModel[] = [];
  private selectedGroup: IManagableObject | null = null;
  private aclMaps: GroupAclMaps = { objectAclById: new Map<number, number>(), groupAclById: new Map<number, number>() };

  @ViewChild(GroupFilterComponent, { static: false }) groupFilter: GroupFilterComponent;
  @ViewChild(ObjectGroupAccessComponent, { static: false }) groupAccess: ObjectGroupAccessComponent;

  constructor(protected injector: Injector) {
    super(injector);
    this.diagnosticsService = injector.get(DiagnosticsService);
    this.objectsService = injector.get(ObjectsService);
  }

  ngAfterViewInit() {
    window.dispatchEvent(new CustomEvent("ComponentLoad", { detail: "Diagnostics" }));
    this.refresh();
  }

  public refresh() {
    this.now = (new Date()).getTime();
    this.loadedObjects = [];
    this.diagnosticsService.GetReport().subscribe(arr => {
      arr.forEach(model => {
        if (model.CompanyName.length == 0)
          model.CompanyName = '***';
      });
      this.loadedObjects = arr;
      this.applyFilter();
      this.resolveAddresses();
    });
    this.companyService.getCompanies().subscribe(c => {
      this.companies = c ?? [];
      this._cd.markForCheck();
    });
    this.objectsService.GetObjectGroups().subscribe(groups => this.applyGroups(groups));
  }

  private resolveAddresses() {
    const withCoords = this.loadedObjects
      .map((obj, idx) => ({ obj, idx }))
      .filter(x => x.obj.Lat !== 0 || x.obj.Lng !== 0);

    if (withCoords.length === 0) return;

    const payload: ICoordinatesViewModel[] = withCoords.map(x => ({
      latitude: x.obj.Lat,
      longitude: x.obj.Lng
    }));

    this.geocodeApi.reverseGeocode(payload).subscribe({
      next: addresses => {
        withCoords.forEach((x, j) => {
          x.obj.Address = addresses[j]?.address ?? '';
        });
        this.applyFilter();
      },
      error: () => {}
    });
  }

  protected override Refresh(): void {
    this.refresh();
  }

  public getObjects(): ObjectDiagnosticsModel[] {
    return this.objects as ObjectDiagnosticsModel[];
  }

  getStatus(status: number): string {
    switch (status) {
      case 0: return 'Blocked';
      case 1: return 'Active';
      case 10: return 'Broken';
      case 11: return 'In service';
      case 15: return 'Not installed';
      case 255: return 'Deleted';
    }
    return '';
  }

  public onExtendedTermChange(data: FilterData) {
    this.filterType = data.searchType;
    this.nameFilter = data.searchFilter;
    this.applyFilter();
  }

  public onCompanyFilterChange(ids: number[] | null | undefined) {
    this.selectedCompanyIds = ids ?? [];
    this.applyFilter();
  }

  public selectGroup(group: IManagableObject) {
    this.selectedGroup = group;
    this.applyFilter();
  }

  protected applyFilter(): void {
    this.oldTimestampCount = 0;
    this.blockedCount = 0;

    const groupIds = (this.selectedGroup?.Id ?? -1) > 0
      ? new Set<number>((this.selectedGroup as ObjectGroupViewModel).objects ?? [])
      : null;
    const companyIds = this.selectedCompanyIds.length > 0
      ? new Set<number>(this.selectedCompanyIds)
      : null;

    this.objects = [];
    for (let i = 0; i < this.loadedObjects.length; i++) {
      const obj = this.loadedObjects[i];

      if (this.nameFilter !== '') {
        if (this.filterType === ObjectFilterType.Name && obj.Name.toLowerCase().indexOf(this.nameFilter.toLowerCase()) < 0)
          continue;
        if (this.filterType === ObjectFilterType.IMEI && obj.IMEI.toLowerCase().indexOf(this.nameFilter.toLowerCase()) < 0)
          continue;
        if (this.filterType === ObjectFilterType.Phone && obj.Phone.toLowerCase().indexOf(this.nameFilter.toLowerCase()) < 0)
          continue;
        if (this.filterType === ObjectFilterType.Equipment && obj.DeviceModel.toLowerCase().indexOf(this.nameFilter.toLowerCase()) < 0)
          continue;
      }

      if (groupIds && !groupIds.has(obj.Id)) continue;
      if (companyIds && !companyIds.has(obj.CompanyId)) continue;

      if (obj.Status === 0) this.blockedCount++;
      if (obj.Time < this.now - this.day * 7) this.oldTimestampCount++;

      this.objects.push(obj);
    }
    this._cd.markForCheck();
  }

  public setShowStatus(showStatus: boolean) {
    this.ShowStatus = showStatus;
  }

  public openEditModal(obj: ObjectDiagnosticsModel) {
    this.prepareModal(EditObjectsComponent);
    this.componentRef.instance.show(obj.Id);
  }

  public deleteDevice(obj: ObjectDiagnosticsModel) {
    if (!confirm(this.translate.instant("Delete") + " " + obj.Name + "?")) return;
    return this.objectsService.DeleteObject(obj.Id).subscribe(() => {
      this.syncService.NotifyEntityChanged("Object", obj.Id);
      for (let i = 0; i < this.loadedObjects.length; i++)
        if (this.loadedObjects[i].Id === obj.Id)
          this.loadedObjects.splice(i, 1);
      this.applyFilter();
    });
  }

  public trackById(index: number, obj: ObjectDiagnosticsModel) {
    return obj.Id;
  }

  public openGroupAccessDialog(group?: IManagableObject) {
    if (group && !this.canEditGroup(group)) return;
    this.groupAccess?.openDialog(group ?? null);
  }

  public deleteGroup(group: IManagableObject) {
    if (!this.canDeleteGroup(group)) return;
    this.objectsService.DeleteGroup(group).subscribe(() => {
      this.loadGroups();
    });
  }

  public onGroupsChanged() {
    this.loadGroups();
  }

  public canEditGroup(group: IManagableObject): boolean {
    return this.accessRights.canEditGroup(group?.Id ?? 0, this.aclMaps);
  }

  public canDeleteGroup(group: IManagableObject): boolean {
    return this.accessRights.canDeleteGroup(group?.Id ?? 0, this.aclMaps);
  }

  private loadGroups() {
    this.objectsService.GetObjectGroups().subscribe(groups => this.applyGroups(groups));
  }

  private applyGroups(groups: ObjectGroupViewModel[] | null | undefined) {
    this.groups = groups ?? [];
    this.aclMaps = this.accessRights.buildAclMaps(this.groups);
    if (this.groupAccess) this.groupAccess.groups = this.groups;
    if (this.groupFilter) this.groupFilter.groups = this.groups as any;
  }
}
